import React, { useEffect, useState, useMemo } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { Grid, Typography } from "@mui/material";
import { useWebServer } from "../../../../../providers";
import _ from 'lodash';
import { validateAddress, validateGps, validateName, validateUuid } from "../../../../../lib/validationHelper";

const AddressFieldOrder = [
  'house_number',
  'road',
  'city',
  'county',
  'postcode',
  'state',
  'continent',
  'country',
  'country_code'
];

const UpdateOrganizationDialog = ({
  isActive,
  organizations,
  selectedOrg,
  setIsActive,
  onConfirm,
  cancelMessage = "Cancel",
}) => {

  const defaultState = {
    uuid: '',
    display_name: '',
    address: '',
    map_center: '',
    address_details: null
  };

  const defaultErrors = {
    uuid: [`UUID must be supplied and unique`],
    display_name: ["Name must be supplied"],
    address: ["Address must be supplied"],
    map_center: ["GPS must be supplied"],
  }

  const [formData, setFormData] = useState(defaultState);
  const [errors, setErrors] = useState({});
  const { getAddressFromGPSLocation } = useWebServer();

  const debouncedUpdateAddress = useMemo(() => {
    return _.debounce(async (gpsValue) => {
      if (!gpsValue || validateGps(gpsValue)?.length !== 0) {
        setFormData((prev) => ({
          ...prev,
          address_details: {}
        }));

        return;
      }
      try {
        let latLon = gpsValue.replace(',', '').split(' ')

        const result = await getAddressFromGPSLocation(latLon[0], latLon[1]);

        if (result?.data?.address) {
          setFormData((prev) => ({
            ...prev,
            address_details: result?.data?.address, // Assuming result is the address string
            address: result?.data?.display_name
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            address_details: {}
          }));
        }

      } catch (error) {
        console.error("Failed to geocode:", error);
      }
    }, 1000);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAddressFromGPSLocation]);

  useEffect(() => {
    validate()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData])

  const originalData = useMemo(() => {
    if (selectedOrg && organizations?.[selectedOrg]) {
      const org = organizations[selectedOrg];
      return {
        uuid: org.uuid,
        display_name: org.display_name || '',
        address: org.address || '',
        map_center: org.map_center || '',
        address_details: org.address_details || null
      };
    }
    return defaultState;
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, organizations]);

  useEffect(() => {
    setFormData(originalData);
    setErrors(selectedOrg ? {} : defaultErrors)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalData]);

  const hasChange = JSON.stringify(formData) !== JSON.stringify(originalData)

  const handleRevert = () => {
    setFormData(originalData);
  };

  const handleClose = () => {
    setIsActive(null);
  };

  const validate = () => {
    let tmpErrors = {}
    tmpErrors.uuid = validateUuid(formData.uuid)
    tmpErrors.address = validateAddress(formData.address)
    tmpErrors.map_center = validateGps(formData.map_center)
    tmpErrors.display_name = validateName(formData.display_name)
    setErrors(tmpErrors)
  }

  const onFieldChange = (field) => (event) => {
    const newVal = event.target.value || "";
    setFormData({ ...formData, [field]: newVal })
    if (field === 'map_center') {
      debouncedUpdateAddress(newVal)
    }
  };

  const isSubmitValid = () =>
    Object.values(errors).every((value) => value.length === 0);

  return !!isActive ? (
    <Dialog
      open={!!isActive}
      onClose={handleClose}
      aria-labelledby="confirm-dialog"
    >
      <DialogTitle id="confirm-dialog">{`${selectedOrg ? 'Update' : 'Create'} Organization`}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{
            "& .MuiTextField-root": { m: 1, width: "25ch" },
          }}
          noValidate
          autoComplete="off"
        >
          <div>
            <TextField
              id="outlined-uuid-field"
              error={
                errors?.uuid?.[0]?.length > 0
              }
              label="UUID"
              value={formData.uuid}
              onChange={onFieldChange("uuid")}
              helperText={errors?.uuid?.[0]}
              disabled={selectedOrg}
            />
            <TextField
              id="outlined-name-field"
              error={
                errors?.display_name?.[0]?.length > 0
              }
              label="Name"
              value={formData.display_name}
              onChange={onFieldChange("display_name")}
              helperText={errors?.display_name?.[0]}
            />
            <TextField
              id="outlined-address-field"
              error={
                errors?.address?.[0]?.length > 0
              }
              label="Address"
              value={formData.address}
              onChange={onFieldChange("address")}
              helperText={errors?.address?.[0]}
            />
            <TextField
              id="outlined-gps-field"
              error={
                errors?.map_center?.[0]?.length > 0
              }
              label="GPS"
              value={formData.map_center}
              onChange={onFieldChange("map_center")}
              helperText={errors?.map_center?.[0]}
            />
            <div>
              {formData?.address_details && Object.keys(formData.address_details).length > 0 && 'Verified Address:'}
              {AddressFieldOrder.map((key) => {
                const value = formData?.address_details?.[key];
                if (!value) return null;
                return (
                  <Grid container key={key} sx={{ alignItems: 'center' }}>

                    <Grid size={5} sx={{ minWidth: '100px' }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                      >
                        {key}:
                      </Typography>
                    </Grid>
                    {/* Value */}
                    <Grid size={7}>
                      <Typography variant="body1" color="text.primary">
                        {formData?.address_details?.[key] || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                )
              })}
            </div>
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        {hasChange && (
          <Button
            variant="outlined"
            onClick={handleRevert}
            id={"dialog-revert"}
          >
            Revert Changes
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleClose}
          color="secondary"
          id={"dialog-cancel"}
        >
          {cancelMessage}
        </Button>
        <Button
          variant="contained"
          disabled={!isSubmitValid()}
          onClick={() => {
            handleClose();
            onConfirm({
              uuid: formData.uuid,
              name: formData.display_name,
              address: formData.address,
              gps: formData.map_center,
              address_details: formData.address_details,
              vectorTypes: ["mosquito", "tick"],
            });
          }}
          id={"dialog-confirm"}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog >
  ) : null;
};

export default UpdateOrganizationDialog;
